import React, { useState, useEffect, useRef } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { PlusIcon, RecordIcon, ArrowUpIcon, PlayIcon, CloseIcon, ReplyIcon, TriangleDownIcon } from '../../utils/icons';
import MediumButton from './MediumButton';
import DropdownMenu from './DropdownMenu';
import Badge from './Badge';

const ChatInput = ({
  onSend,
  onAttach,
  onRecord,
  placeholder = "Ask anything",
  selectedSong = null,
  onClearSelectedSong = null,
  onSuggestionClick = null,
  referencedLyrics = null,
  onClearReferencedLyrics = null,
  isStyleReference = false, // New prop to indicate if referenced text is styles/genres
  selectedVersion,
  onVersionChange
}) => {
  const [message, setMessage] = useState('');
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const versionButtonRef = useRef(null);

  // Pre-fill message when style reference is set
  useEffect(() => {
    if (referencedLyrics && isStyleReference) {
      setMessage('Change the genre to ');
    } else if (!referencedLyrics) {
      // Clear message when reference is cleared
      setMessage('');
    }
  }, [referencedLyrics, isStyleReference]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && onSend) {
      onSend(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const suggestions = [
    'EXTEND SONG BY 30S',
    'EDIT LYRICS',
    'CHANGE GENRE',
    'REUSE STYLES AND LYRICS',
    'OPEN IN CUSTOM'
  ];

  const lyricsSuggestions = [
    'MAKE HAPPIER',
    'CHANGE THE FIRST LINE',
    'MAKE IT RHYME MORE',
    'TRY ANOTHER VARIATION'
  ];

  const genreSuggestions = [
    'K-POP',
    'POP',
    'COUNTRY',
    'ROCK',
    'HOUSE',
    'ELECTRONIC',
    'EDM',
    'FOLK',
    'INDIE',
    'ALTERNATIVE'
  ];

  // Model options for the dropdown
  const modelOptions = [
    {
      value: "v4.5",
      label: (
        <div className="flex items-center gap-1">
          <span>v4.5</span>
          <Badge text="Beta" variant="secondary" size="small" />
          <Badge text="Pro" variant="tertiary" size="small" />
        </div>
      ),
      description: "Smarter prompts, better genre mashups, faster generations, max 8 min"
    },
    {
      value: "v4",
      label: (
        <div className="flex items-center gap-1">
          <span>v4</span>
          <Badge text="Pro" variant="tertiary" size="small" />
        </div>
      ),
      description: "Improved vocal quality\nmax 4 min"
    },
    {
      value: "v3.5",
      label: "v3.5",
      description: "Better song structure\nmax 4 min"
    },
    {
      value: "v3",
      label: "v3",
      description: "Older\nmax 2 min"
    },
    {
      value: "v2",
      label: "v2",
      description: "Vintage\nmax 2 min"
    }
  ];

  const handleVersionClick = () => {
    setShowVersionDropdown(!showVersionDropdown);
  };

  const handleVersionSelect = (newVersion) => {
    onVersionChange?.(newVersion);
    setShowVersionDropdown(false);
  };

  return (
    <div className="pt-0 px-4 pb-4" style={{ backgroundColor: colors.bg.primary }}>
      <form onSubmit={handleSubmit}>
        <div
          className="flex flex-col rounded-2xl"
          style={{ backgroundColor: colors.bg.fog.thin }}
        >
          {/* Referenced Lyrics Header - with song thumbnail when song is selected */}
          {referencedLyrics && selectedSong && (
            <div
              className="flex items-center justify-between px-4 py-3 rounded-t-2xl"
              style={{ backgroundColor: colors.bg.fog.thin }}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="relative w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{
                    backgroundImage: selectedSong.image ? `url(${selectedSong.image})` : 'none',
                    backgroundColor: colors.bg.fog.thin,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="absolute inset-0 bg-black/50 rounded-lg" />
                  <div className="relative w-4 h-4 z-10 flex items-center justify-center" style={{ color: colors.text.primary }}>
                    <ReplyIcon />
                  </div>
                </div>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <p
                    className="truncate uppercase"
                    style={{
                      ...typography.timecode,
                      color: colors.text.primary
                    }}
                  >
                    {selectedSong.title}
                  </p>
                  <p
                    className="truncate"
                    style={{
                      ...typography.xsmall.regular,
                      color: colors.text.tertiary
                    }}
                  >
                    "{referencedLyrics}"
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClearReferencedLyrics}
                className="w-6 h-6 flex items-center justify-center hover:opacity-80 transition-opacity flex-shrink-0"
                style={{ color: colors.text.primary }}
              >
                <CloseIcon />
              </button>
            </div>
          )}

          {/* Referenced Lyrics Header - without song thumbnail */}
          {referencedLyrics && !selectedSong && (
            <div
              className="flex items-center justify-between px-4 py-3 rounded-t-2xl"
              style={{ backgroundColor: colors.bg.fog.thin }}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-6 h-6 flex-shrink-0" style={{ color: colors.text.primary }}>
                  <ReplyIcon />
                </div>
                <p
                  className="truncate"
                  style={{
                    ...typography.small.regular,
                    color: colors.text.primary
                  }}
                >
                  "{referencedLyrics}"
                </p>
              </div>
              <button
                type="button"
                onClick={onClearReferencedLyrics}
                className="w-6 h-6 flex items-center justify-center hover:opacity-80 transition-opacity flex-shrink-0"
                style={{ color: colors.text.primary }}
              >
                <CloseIcon />
              </button>
            </div>
          )}

          {/* Selected Song Header - only show if no referenced lyrics */}
          {selectedSong && !referencedLyrics && (
            <div
              className="flex items-center justify-between px-4 py-3 rounded-t-2xl"
              style={{ backgroundColor: colors.bg.fog.thin }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-lg bg-cover bg-center flex items-center justify-center"
                  style={{
                    backgroundImage: selectedSong.image ? `url(${selectedSong.image})` : 'none',
                    backgroundColor: colors.bg.fog.thin
                  }}
                >
                  <div className="w-4 h-4" style={{ color: colors.text.primary }}>
                  </div>
                </div>
                <p
                  className="uppercase"
                  style={{
                    ...typography.timecode,
                    color: colors.text.primary
                  }}
                >
                  {selectedSong.title}
                </p>
              </div>
              <button
                type="button"
                onClick={onClearSelectedSong}
                className="w-6 h-6 flex items-center justify-center hover:opacity-80 transition-opacity"
                style={{ color: colors.text.primary }}
              >
                <CloseIcon />
              </button>
            </div>
          )}

          {/* Suggestions */}
          {referencedLyrics && (
            <div className="flex gap-2 items-center px-4 pt-3 pb-0 overflow-x-auto scrollbar-hide">
              {(isStyleReference ? genreSuggestions : lyricsSuggestions).map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    if (isStyleReference) {
                      // For genre suggestions, append to the message
                      setMessage(prev => {
                        // If message ends with a genre already, replace with comma-separated list
                        const trimmed = prev.trim();
                        if (trimmed.endsWith(',')) {
                          return `${prev} ${suggestion.toLowerCase()}`;
                        } else if (trimmed === 'Change the genre to') {
                          return `${prev}${suggestion.toLowerCase()}`;
                        } else {
                          return `${prev}, ${suggestion.toLowerCase()}`;
                        }
                      });
                    } else {
                      // For lyrics suggestions, use the callback
                      onSuggestionClick?.(suggestion);
                    }
                  }}
                  className="px-2 py-2 rounded-full flex-shrink-0 hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: colors.bg.fog.thin,
                    ...typography.timecode,
                    color: colors.text.primary
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
          {selectedSong && !referencedLyrics && (
            <div className="flex gap-2 items-center px-4 pt-3 pb-0 overflow-x-auto scrollbar-hide">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => onSuggestionClick?.(suggestion)}
                  className="px-2 py-2 rounded-full flex-shrink-0 hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: colors.bg.fog.thin,
                    ...typography.timecode,
                    color: colors.text.primary
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Input Area */}
          <div className="flex items-start pt-4 px-4 pb-0">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectedSong ? "Ask anything" : placeholder}
              className="flex-1 bg-transparent border-none outline-none"
              style={{
                ...typography.medium.title,
                color: colors.text.primary,
                '::placeholder': {
                  color: colors.text.inactive
                }
              }}
            />
          </div>

          {/* Actions Row */}
          <div className="flex items-center justify-between px-4 py-3">
            <MediumButton
              variant="secondary"
              leftIcon={<PlusIcon />}
              onClick={onAttach}
            />

            {message.trim() ? (
              <MediumButton
                variant="aura"
                leftIcon={<ArrowUpIcon />}
                onClick={handleSubmit}
              />
            ) : (
              <button
                type="button"
                onClick={onRecord}
                className="flex items-center justify-center w-6 h-6 hover:opacity-90 transition-opacity"
                style={{ color: colors.text.primary }}
              >
                <RecordIcon />
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;